import axios from 'axios';

const API_BASE = process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1 || '';

export interface SubscriptionPlan {
  _id?: string;
  name: string;
  description: string;
  hostLimit: number;
  hardwareLimit: number;
  price: {
    usd: number;
    eur: number;
  };
  billingCycle: 'monthly' | 'yearly';
  features: string[];
  duration: number;
  isActive: boolean;
  hidden: boolean;
  isTrial?: boolean;
  product?: string | SubscriptionProduct;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionProduct {
  _id?: string;
  product_name?: string;
  heading_main?: string;
  heading_sub?: string;
  icon_image?: {
    url?: string;
    title?: string;
  };
}

export interface UserSubscription {
  _id: string;
  user: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  subscriptionPlan: SubscriptionPlan;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  startDate: string;
  endDate: string;
  nextBillingDate?: string | null;
  autoRenew: boolean;
  orderId?: string | null;
  additionalHosts?: number;
  additionalHardware?: number;
  selectedDuration?: number;
  // New fields (similar to Order)
  txnId?: string;
  txnStatus?: 'pending' | 'completed' | 'failed' | 'expired';
  amount?: number;
  amountPerMonth?: number;
  currency?: 'usd' | 'eur';
  product?: string | SubscriptionProduct;
  os?: ('windows' | 'mac' | 'linux')[];
  supports?: string[];
  interId?: string;
  productSerial?: string;
  url?: {
    windows?: string;
    mac?: string;
    linux?: string;
  };
  maxWorkflowRunsPerProduct?: number;
  paymentInfo?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface CheckoutResult {
  // true when the plan was free and the subscription is already active (no payment step)
  free: boolean;
  // Stripe checkout URL to redirect to (paid plans only)
  url?: string;
  // the activated subscription (free plans only)
  subscription?: UserSubscription;
}

export interface GetPlansFilters {
  isActive?: boolean;
  billingCycle?: 'monthly' | 'yearly';
  hidden?: boolean;
}

export const subscriptionService = {
  // Get plans (Public)
  getPlans: async (filters?: GetPlansFilters): Promise<SubscriptionPlan[]> => {
    const params = new URLSearchParams();
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.billingCycle) params.append('billingCycle', filters.billingCycle);
    if (filters?.hidden !== undefined) params.append('hidden', String(filters.hidden));

    const res = await axios.get(`${API_BASE}/subscriptions/plans?${params.toString()}`);
    return res.data.data || res.data || [];
  },

  // Get plan by ID (Public)
  getPlanById: async (id: string): Promise<SubscriptionPlan> => {
    const res = await axios.get(`${API_BASE}/subscriptions/plans/${id}`);
    return res.data.data || res.data;
  },

  // Create subscription (User - JWT Required)
  createSubscription: async (data: {
    subscriptionPlanId: string;
    orderId?: string;
    startDate?: string;
    autoRenew?: boolean;
  }): Promise<UserSubscription> => {
    const token = localStorage.getItem('auth-token');
    const res = await axios.post(`${API_BASE}/subscriptions/my-subscriptions`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.data || res.data;
  },

  // Get user subscriptions (JWT Required)
  getUserSubscriptions: async (): Promise<UserSubscription[]> => {
    const token = localStorage.getItem('auth-token');
    const res = await axios.get(`${API_BASE}/subscriptions/my-subscriptions`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    return res.data.data || res.data || [];
  },

  // Get user subscription by ID (JWT Required)
  getUserSubscriptionById: async (id: string): Promise<UserSubscription> => {
    const token = localStorage.getItem('auth-token');
    const res = await axios.get(`${API_BASE}/subscriptions/my-subscriptions/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.data || res.data;
  },

  // Cancel subscription (JWT Required)
  cancelSubscription: async (subscriptionId: string): Promise<UserSubscription> => {
    const token = localStorage.getItem('auth-token');
    const res = await axios.patch(
      `${API_BASE}/subscriptions/my-subscriptions/${subscriptionId}/cancel`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res.data.data || res.data;
  },

  // Update auto-renew (JWT Required)
  updateAutoRenew: async (
    subscriptionId: string,
    autoRenew: boolean
  ): Promise<UserSubscription> => {
    const token = localStorage.getItem('auth-token');
    const res = await axios.patch(
      `${API_BASE}/subscriptions/my-subscriptions/${subscriptionId}/auto-renew`,
      { autoRenew },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res.data.data || res.data;
  },

  // Delete subscription (JWT Required)
  deleteSubscription: async (subscriptionId: string): Promise<void> => {
    const token = localStorage.getItem('auth-token');
    await axios.delete(`${API_BASE}/subscriptions/my-subscriptions/${subscriptionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  // Admin: Create plan
  createPlan: async (data: {
    name: string;
    description: string;
    hostLimit: number;
    hardwareLimit: number;
    price: {
      usd: number;
      eur: number;
    };
    billingCycle: 'monthly' | 'yearly';
    features?: string[];
    duration?: number;
    isActive?: boolean;
    hidden?: boolean;
    isTrial?: boolean;
  }): Promise<SubscriptionPlan> => {
    const token = localStorage.getItem('auth-token');
    const res = await axios.post(`${API_BASE}/subscriptions/plans`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.data || res.data;
  },

  // Admin: Update plan
  updatePlan: async (
    planId: string,
    data: Partial<{
      name: string;
      description: string;
      hostLimit: number;
      hardwareLimit: number;
      price: {
        usd: number;
        eur: number;
      };
      billingCycle: 'monthly' | 'yearly';
      features: string[];
      duration: number;
      isActive: boolean;
      hidden: boolean;
      isTrial?: boolean;
    }>
  ): Promise<SubscriptionPlan> => {
    const token = localStorage.getItem('auth-token');
    const res = await axios.patch(`${API_BASE}/subscriptions/plans/${planId}`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.data || res.data;
  },

  // Admin: Delete plan
  deletePlan: async (planId: string): Promise<void> => {
    const token = localStorage.getItem('auth-token');
    await axios.delete(`${API_BASE}/subscriptions/plans/${planId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  // Create Stripe checkout session for subscription
  // Based on Stripe Payment Integration Guide
  createCheckoutSession: async (
    planId: string,
    currency: 'usd' | 'eur',
    options?: {
      additionalHosts?: number;
      additionalHardware?: number;
      supports: string[]; // Required - selected hosts
      selectedDuration?: number;
      os?: ('windows' | 'mac' | 'linux')[];
      maxWorkflowRunsPerProduct?: number;
    }
  ): Promise<CheckoutResult> => {
    const token = localStorage.getItem('auth-token');
    const requestBody: any = {
      subscriptionPlanId: planId,
      currency,
      supports: options?.supports || [], // Required field
    };

    // Add optional fields if provided
    if (options?.additionalHosts !== undefined) {
      requestBody.additionalHosts = options.additionalHosts;
    }
    if (options?.additionalHardware !== undefined) {
      requestBody.additionalHardware = options.additionalHardware;
    }
    if (options?.selectedDuration !== undefined) {
      requestBody.selectedDuration = options.selectedDuration;
    }
    if (options?.os && options.os.length > 0) {
      requestBody.os = options.os;
    }
    if (options?.maxWorkflowRunsPerProduct !== undefined) {
      requestBody.maxWorkflowRunsPerProduct = options.maxWorkflowRunsPerProduct;
    }

    const res = await axios.post(`${API_BASE}/subscriptions/checkout`, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`, // According to Stripe integration guide
      },
    });

    // Free plan: backend activates the subscription immediately and returns it
    // instead of a Stripe checkout session (Stripe cannot charge a 0 amount).
    if (res.data?.success && res.data?.subscription && !res.data?.session) {
      return { free: true, subscription: res.data.subscription };
    }

    // Paid plan response format: { success: true, session: { url: "..." } }
    if (res.data?.success && res.data?.session?.url) {
      return { free: false, url: res.data.session.url };
    }

    // Fallback for different response formats
    return {
      free: false,
      url: res.data?.session?.url || res.data?.url || res.data?.checkoutUrl || '',
    };
  },
};
